import React, { useEffect, useMemo, useRef, useState } from "react";
import AILightComponent from '../../components/AILightComponent';

const DEFAULT_STATS = {
  criticalSignals: 0,
  mediumSignals: 0,
  lowSignals: 0,
  totalSignals: 0,
};

const MSMEOwnerAI = ({ selectedLanguage = "en" }) => {
  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState("");
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [agentStatus, setAgentStatus] = useState("");
  const [actions, setActions] = useState([]);
  const [agentState, setAgentState] = useState(null);
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [agentPipeline, setAgentPipeline] = useState([]);
  const [toolExecutions, setToolExecutions] = useState([]);
  const [memoryTimeline, setMemoryTimeline] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const prevStatusRef = useRef(null);
  const [activeView, setActiveView] = useState("today");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [activeAssistIndex, setActiveAssistIndex] = useState(null);

  useEffect(() => {
    if (
      agentState?.status &&
      agentState.status !== prevStatusRef.current
    ) {
      setShowAgentPopup(true);

      setTimeout(() => {
        setShowAgentPopup(false);
      }, 2500);

      prevStatusRef.current = agentState.status;
    }
  }, [agentState]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          "http://localhost:3001/api/agent/snapshot"
        );

        const data = await res.json();

        setStats({
          criticalSignals:
            data.criticalSignals || 0,

          mediumSignals:
            data.mediumSignals || 0,

          lowSignals:
            data.lowSignals || 0,

          totalSignals:
            data.totalSignals || 0,
        });

        setSummary(data.summary || "");

        setAgentState(data);

        setAgentPipeline(
          data.agentPipeline || []
        );

        setToolExecutions(
          data.toolExecutions || []
        );

        setMemoryTimeline(
          data.memoryTimeline || []
        );

        setReflections(
          data.reflections || []
        );
      } catch (err) {
        console.error(err);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const fetchAIInsights = async () => {
    try {
      setLoading(true);

      setError("");

      const response = await fetch(
        "http://localhost:3001/api/ai/query",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            role: "msmeowner",

            module: "msme",

            query:
              "MSME owner operational risk analysis and business cognitive diagnostics",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          "AI Request Failed"
        );
      }

      const data = await response.json();

      setSignals(
        data.signals ||
          data.cognition?.signals ||
          []
      );

      setSummary(
        data.summary ||
          data.cognition?.summary ||
          ""
      );

      setStats({
        criticalSignals:
          data.criticalSignals ??
          data.cognition
            ?.criticalSignals ??
          0,

        mediumSignals:
          data.mediumSignals ??
          data.cognition
            ?.mediumSignals ??
          0,

        lowSignals:
          data.lowSignals ??
          data.cognition?.lowSignals ??
          0,

        totalSignals:
          data.totalSignals ??
          data.cognition
            ?.totalSignals ??
          0,
      });

      setAgentStatus(data.status || "");

      setActions(data.actions || []);

      setAgentPipeline(
        data.agentPipeline || []
      );

      setToolExecutions(
        data.toolExecutions || []
      );

      setMemoryTimeline(
        data.memoryTimeline || []
      );

      setReflections(
        data.reflections || []
      );
    } catch (err) {
      console.error(err);

      setError(
        "Failed to load MSME cognitive intelligence"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAIInsights();
  }, []);

  const severityColor = (
    severity
  ) => {
    switch (
      severity?.toLowerCase()
    ) {
      case "high":
        return "text-red-400";

      case "medium":
        return "text-yellow-300";

      case "low":
        return "text-green-400";

      default:
        return "text-gray-400";
    }
  };

//   const filteredSignals = useMemo(() => {
//     let filtered = [...signals];

//     // PERIOD FILTER
//     if (activeView === "today") {
//         filtered = filtered.filter(
//         (s) => s.period === "today"
//         );
//     }

//     // SEARCH FILTER
//     if (searchTerm.trim()) {
//         filtered = filtered.filter(
//         (s) =>
//             s.insight
//             ?.toLowerCase()
//             .includes(
//                 searchTerm.toLowerCase()
//             ) ||
//             s.type
//             ?.toLowerCase()
//             .includes(
//                 searchTerm.toLowerCase()
//             )
//         );
//     }
//     return filtered;
//   }, [signals, activeView, searchTerm]);

const filteredSignals = useMemo(() => {
  let filtered = [...signals];

  // PERIOD FILTER
  if (activeView === "today") {
    filtered = filtered.filter(
      (s) =>
        s.period === "today" ||
        s.period === "daily" ||
        !s.period
    );
  }

  if (activeView === "weekly") {
    filtered = filtered.filter(
      (s) => s.period === "weekly"
    );
  }

  if (activeView === "monthly") {
    filtered = filtered.filter(
      (s) => s.period === "monthly"
    );
  }

  // SEARCH FILTER
  if (searchTerm.trim()) {
    filtered = filtered.filter(
      (s) =>
        safeText(s.insight)
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          ) ||
        safeText(s.type)
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          )
    );
  }

  return filtered;
}, [signals, activeView, searchTerm]);


const speakSignal = (text) => {

  if (!window.speechSynthesis) {
    alert("Text to Speech not supported");
    return;
  }

  const utterance =
    new SpeechSynthesisUtterance(text);

  utterance.lang =
    selectedLanguage === "hi"
      ? "hi-IN"
      : "en-US";

  utterance.rate = 1;

  utterance.pitch = 1;

  speechSynthesis.cancel();

  speechSynthesis.speak(utterance);
};

const speakSignals = () => {

  if (!window.speechSynthesis) {
    alert("Text to Speech not supported");
    return;
  }

  setIsSpeaking(true);

  const combinedText = filteredSignals.map((signal, index) =>
            `Signal ${index + 1}. 
            ${safeText(signal.type)}. 
            ${safeText(signal.insight)}. 
            Severity ${safeText(signal.severity)}.`
        ).join(" ");

  const utterance = new SpeechSynthesisUtterance(combinedText);

  utterance.lang = selectedLanguage === "hi" ? "hi-IN" : "en-US";

  utterance.rate = 1;

  utterance.pitch = 1;

  utterance.onend = () => {
    setIsSpeaking(false);
  };

  speechSynthesis.cancel();

  speechSynthesis.speak(
    utterance
  );
};

const stopSpeaking = () => {

  speechSynthesis.cancel();

  setIsSpeaking(false);
};

  const safeText = (value) => {
    if (!value) return "N/A";

    if (typeof value === "string")
      return value;

    return (
      value?.[selectedLanguage] ||
      value?.en ||
      "N/A"
    );
  };

  const cognitiveHealth = useMemo(() => {
    if (
      stats.criticalSignals >= 5
    ) {
      return {
        label:
          "Critical Operational Stress",
        color:
          "bg-red-950 border border-red-500/30",
      };
    }

    if (
      stats.criticalSignals >= 2
    ) {
      return {
        label:
          "Elevated Business Risk",
        color:
          "bg-yellow-900 border border-yellow-500/30",
      };
    }

    return {
      label:
        "Operationally Stable",
      color:
        "bg-green-900 border border-green-500/30",
    };
  }, [stats]);

  return (
    <div className="rounded-3xl border border-slate-800 bg-gradient-to-b from-slate-950 via-slate-900 to-black p-4 text-white shadow-2xl lg:p-6 space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg lg:text-xl font-black text-yellow-200">
            MSME Owner Cognitive AI
          </h2>
          <p className="mt-2 text-xs leading-6 text-gray-400 lg:text-sm">
            Real-time operational intelligence,
            business risk cognition,
            workflow diagnostics,
            productivity signals,
            compliance tracking,
            cash-flow stress detection,
            and AI-assisted MSME transformation.
          </p>
        </div>
        <button
          onClick={fetchAIInsights}
          className="w-40 rounded-2xl bg-teal-700 px-4 py-3 text-xs font-bold text-yellow-200 transition-all hover:bg-teal-600 lg:w-64 lg:text-sm"
        >
          <i className="fa-solid fa-rotate-right mr-2"></i>
          Refresh AI
        </button>
      </div>
      {loading && (
        <div className="rounded-2xl border border-slate-700 bg-slate-900 p-4 text-sm text-gray-300 animate-pulse">
          AI Agents are analysing MSME operational intelligence...
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-red-700 bg-red-950 p-4 text-sm text-red-300">
          {error}
        </div>
      )}
      {!loading && (
        <div
          className={`${cognitiveHealth.color} rounded-2xl p-5`}
        >
          <div className="text-xs uppercase tracking-wider text-gray-300">
            Business Cognitive Status
          </div>

          <div className="mt-2 text-xl font-black text-white">
            {cognitiveHealth.label}
          </div>
        </div>
      )}
      {agentState && (
        <div className="rounded-2xl border border-blue-800 bg-blue-950/40 p-4">
          <div className="flex items-center justify-between">

            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-yellow-300">
                Live Agent State
              </div>

              <div className="mt-2 text-sm text-white">
                {agentState.status}
              </div>
            </div>

            <div className="text-[10px] text-gray-400">
              {agentState.lastUpdated}
            </div>
          </div>

          <p className="mt-3 text-sm leading-6 text-gray-300">
            {agentState.summary}
          </p>
        </div>
      )}
      {showAgentPopup &&
        agentState && (
          <div className="fixed right-5 top-5 z-50 rounded-2xl border border-blue-700 bg-blue-900 p-4 shadow-2xl animate-pulse">

            <div className="text-xs font-bold text-yellow-300">
              🤖 AGENT ACTIVE
            </div>

            <div className="mt-2 text-sm font-semibold text-white">
              {agentState.status}
            </div>

            <div className="mt-1 text-xs text-gray-300">
              {agentState.message}
            </div>
          </div>
        )}
      {agentStatus && (
        <div className="rounded-xl bg-blue-900/40 p-3 text-xs text-blue-100">
          Agent Status : {agentStatus}
        </div>
      )}
      {actions?.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-black/50 p-4">

          <h3 className="text-xs lg:text-sm font-bold text-yellow-200">
            Autonomous Agent Actions
          </h3>

          <div className="mt-4 space-y-2">
            {actions.map((a, i) => (
              <div
                key={i}
                className="rounded-xl border border-slate-800 bg-slate-900 p-3 text-xs text-green-300"
              >
                {a.type} → {a.message}
              </div>
            ))}
          </div>
        </div>
      )}
      {agentPipeline?.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-950 p-4">

          <h3 className="text-xs lg:text-sm font-bold text-yellow-200">
            Agent Cognitive Pipeline
          </h3>

          <div className="mt-4 flex flex-wrap gap-2">

            {agentPipeline.map(
              (step, idx) => (
                <div
                  key={idx}
                  className={`rounded-full px-4 py-2 text-xs font-semibold
                  ${
                    step.status ===
                    "completed"
                      ? "bg-green-700 text-white"
                      : step.status ===
                        "running"
                      ? "bg-blue-700 text-white animate-pulse"
                      : "bg-slate-700 text-gray-300"
                  }`}
                >
                  {step.step.toUpperCase()}
                </div>
              )
            )}
          </div>
        </div>
      )}
      {toolExecutions?.length > 0 && (
        <div className="rounded-2xl border border-indigo-800 bg-indigo-950/40 p-4">

          <h3 className="text-xs lg:text-sm font-bold text-cyan-300">
            Autonomous Tool Execution
          </h3>

          <div className="mt-4 space-y-3">

            {toolExecutions.map(
              (tool, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-slate-800 bg-black/40 p-4"
                >
                  <div className="flex items-center justify-between">

                    <div className="text-xs font-bold text-cyan-200">
                      {tool.tool}
                    </div>

                    <div className="text-[10px] text-green-400">
                      {tool.status}
                    </div>
                  </div>

                  <div className="mt-2 text-xs leading-6 text-gray-300">
                    {tool.message}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
      {memoryTimeline?.length > 0 && (
        <div className="rounded-2xl border border-purple-800 bg-purple-950/40 p-4">

          <h3 className="text-xs lg:text-sm font-bold text-pink-300">
            Operational Memory Timeline
          </h3>

          <div className="mt-4 space-y-4">

            {memoryTimeline.map(
              (m, idx) => (
                <div
                  key={idx}
                  className="border-l-2 border-pink-500 pl-4"
                >
                  <div className="text-[10px] text-gray-500">
                    {m.date}
                  </div>

                  <div className="mt-1 text-sm text-white">
                    {m.event}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
      {reflections?.length > 0 && (
        <div className="rounded-2xl border border-orange-700 bg-orange-950/30 p-4">

          <h3 className="text-xs lg:text-sm font-bold text-orange-300">
            AI Reflection Loop
          </h3>

          <div className="mt-4 space-y-3">

            {reflections.map(
              (r, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-black/30 p-4"
                >
                  <div className="text-xs font-bold text-yellow-200">
                    {r.question}
                  </div>

                  <div className="mt-2 text-sm leading-6 text-gray-300">
                    {r.answer}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
      {!!summary && (
        <div className="rounded-2xl border border-purple-800 bg-purple-900/40 p-5">

          <h3 className="text-xs lg:text-sm font-bold text-yellow-200">
            Cognitive Summary
          </h3>

          <p className="mt-3 text-sm leading-7 text-white">
            {summary}
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">

        <div className="rounded-2xl bg-red-900 p-4">
          <div className="text-3xl font-black">
            {stats.criticalSignals}
          </div>

          <div className="mt-2 text-xs text-red-100">
            Critical Signals
          </div>
        </div>

        <div className="rounded-2xl bg-yellow-700 p-4">
          <div className="text-3xl font-black">
            {stats.mediumSignals}
          </div>

          <div className="mt-2 text-xs text-yellow-100">
            Medium Signals
          </div>
        </div>

        <div className="rounded-2xl bg-green-700 p-4">
          <div className="text-3xl font-black">
            {stats.lowSignals}
          </div>

          <div className="mt-2 text-xs text-green-100">
            Low Signals
          </div>
        </div>

        <div className="rounded-2xl bg-teal-700 p-4">
          <div className="text-3xl font-black">
            {stats.totalSignals}
          </div>

          <div className="mt-2 text-xs text-teal-100">
            Total Signals
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {signals.length === 0 &&
          !loading && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 text-sm text-gray-400">
              No operational signals detected.
            </div>
          )}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-2">
                {["today", "weekly", "monthly"].map(
                (tab) => (
                    <button
                    key={tab}
                    onClick={() =>
                        setActiveView(tab)
                    }
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition-all
                    ${
                        activeView === tab
                        ? "bg-yellow-400 text-black"
                        : "bg-slate-800 text-gray-300"
                    }`}
                    >
                    {tab.toUpperCase()}
                    </button>
                )
                )}
            </div>
            {/* <div className="flex gap-2">
                <input
                type="text"
                placeholder="Search signals..."
                value={searchTerm}
                onChange={(e) =>
                    setSearchTerm(
                    e.target.value
                    )
                }
                className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-white outline-none"
                />

                {!isSpeaking ? (
                <button
                    onClick={speakSignals}
                    className="rounded-xl bg-blue-700 px-4 py-2 text-xs font-bold"
                >
                    🔊 Speak
                </button>
                ) : (
                <button
                    onClick={stopSpeaking}
                    className="rounded-xl bg-red-700 px-4 py-2 text-xs font-bold"
                >
                    ⏹ Stop
                </button>
                )}
            </div> */}
        </div>
        <div className="flex flex-wrap gap-2">
            <input
                type="text"
                placeholder="Search signals..."
                value={searchTerm}
                onChange={(e) =>
                setSearchTerm(e.target.value)
                }
                className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-white outline-none"
            />

            {!isSpeaking ? (
                <button
                onClick={speakSignals}
                className="rounded-lg bg-blue-700 px-3 py-2 text-[11px] font-bold text-white hover:bg-blue-600"
                >
                🔊 All Signals
                </button>
            ) : (
                <button
                onClick={stopSpeaking}
                className="rounded-lg bg-red-700 px-3 py-2 text-[11px] font-bold text-white hover:bg-red-600"
                >
                ⏹ Stop
                </button>
            )}
        </div>

        {filteredSignals.map(
          (signal, index) => (
            <div
              key={index}
              className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5"
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">

                <div className="text-sm font-bold text-teal-300">
                  {safeText(signal.type)}
                </div>

                <div
                  className={`text-xs font-bold uppercase tracking-wider ${severityColor(signal.severity)}`}
                >
                  {signal.severity}
                </div>
              </div>

              <p className="mt-4 text-sm leading-7 text-gray-300">
                {safeText(signal.insight)}
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                    onClick={() =>
                        speakSignal(
                        `${safeText(signal.type)}. ${safeText(signal.insight)}`
                        )
                    }
                    className="text-xs lg:text-sm w-32 lg:w-48 rounded-md bg-cyan-700 px-2 py-1 font-semibold text-white hover:bg-cyan-600"
                    >
                    🔊 Speak
                </button>
                <button
                    onClick={() =>
                        setActiveAssistIndex(
                        activeAssistIndex === index ? null : index
                        )
                    }
                    className="text-xs lg:text-sm w-32 lg:w-48 rounded-md bg-purple-700 px-2 py-1 text-[10px] font-semibold text-white hover:bg-purple-600"
                    >
                    🤖 What To Do
                </button>
              </div>
              {activeAssistIndex === index && (
                    <div className="mt-3">
                        <AILightComponent param1={"msme_owner_task_assistance"} param2={safeText(signal.insight)}/>
                    </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {signal.department && (
                  <span className="rounded-full bg-slate-800 px-3 py-1 text-[10px] text-gray-300">
                    {signal.department}
                  </span>
                )}
                {signal.category && (
                  <span className="rounded-full bg-slate-800 px-3 py-1 text-[10px] text-gray-300">
                    {signal.category}
                  </span>
                )}

                {signal.impact && (
                  <span className="rounded-full bg-red-900/40 px-3 py-1 text-[10px] text-red-200">
                    Impact : {signal.impact}
                  </span>
                )}
              </div>

              <div className="mt-4 text-[10px] uppercase tracking-wider text-gray-500">
                {signal.source ||
                  "MSME Cognitive AI"}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MSMEOwnerAI;